/**
 * Global Branding Constants
 *
 * Centralized branding configuration for the platform.
 * Update these values to rebrand the entire application.
 */

export const BRAND = {
  name: "ICSC BOSS",           // Full product name
  org: "ICSC Accreditation",   // Organization name
  short: "ICSC",               // Short brand name
  tagline: "Business Operations & Standards System"
};

// Color Palette
export const COLORS = {
  primary: "#3b82f6",          // Blue-500
  primaryHover: "#2563eb",     // Blue-600
  primaryLight: "#dbeafe",     // Blue-100
  secondary: "#8b5cf6",        // Violet-500
  secondaryHover: "#7c3aed",   // Violet-600
  success: "#10b981",          // Green-500
  successHover: "#059669",     // Green-600
  successLight: "#d1fae5",     // Green-100
  warning: "#f59e0b",          // Amber-500
  warningHover: "#d97706",     // Amber-600
  warningLight: "#fef3c7",     // Amber-100
  error: "#ef4444",            // Red-500
  errorHover: "#dc2626",       // Red-600
  errorLight: "#fee2e2",       // Red-100
  info: "#06b6d4",             // Cyan-500
  infoLight: "#cffafe",        // Cyan-100
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
  },
  white: "#ffffff",
  black: "#000000",
  background: "#f9fafb",       // Gray-50
  surface: "#ffffff",          // White
  border: "#e5e7eb",           // Gray-200
  text: {
    primary: "#111827",        // Gray-900
    secondary: "#4b5563",      // Gray-600
    muted: "#9ca3af",          // Gray-400
    inverse: "#ffffff",        // White
  }
};

// Spacing System (in pixels, converted to rem in CSS)
export const SPACING = {
  xs: "0.25rem",    // 4px
  sm: "0.5rem",     // 8px
  md: "1rem",       // 16px
  lg: "1.5rem",     // 24px
  xl: "2rem",       // 32px
  "2xl": "3rem",    // 48px
  "3xl": "4rem",    // 64px
  "4xl": "6rem",    // 96px
};

// Typography System
export const TYPOGRAPHY = {
  fontFamily: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    mono: "'Fira Code', 'Courier New', monospace",
  },
  fontSize: {
    xs: "0.75rem",      // 12px
    sm: "0.875rem",     // 14px
    base: "1rem",       // 16px
    lg: "1.125rem",     // 18px
    xl: "1.25rem",      // 20px
    "2xl": "1.5rem",    // 24px
    "3xl": "1.875rem",  // 30px
    "4xl": "2.25rem",   // 36px
    "5xl": "3rem",      // 48px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  }
};

// Border Radius System
export const RADIUS = {
  none: "0",
  sm: "0.25rem",    // 4px
  md: "0.375rem",   // 6px
  lg: "0.5rem",     // 8px
  xl: "0.75rem",    // 12px
  "2xl": "1rem",    // 16px
  full: "9999px",   // Circular
};

// Shadow System
export const SHADOWS = {
  sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
  none: "none",
};

// Animation Durations
export const ANIMATION = {
  fast: "150ms",
  normal: "200ms",
  slow: "300ms",
  slower: "500ms",
};

// Z-Index Layers
export const Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// Breakpoints for responsive design
export const BREAKPOINTS = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

export default BRAND;
